import React, { useState } from "react";
import { useSelector } from "react-redux";
import { TailSpin } from "react-loader-spinner";
import { formatDistanceToNow } from "date-fns";

import config from "config";

import { LOADING_STATUSES as STATUS } from "constants/common";
import { formatDate } from "helpers/format-date";
import {
  getSelectedPhoneCall,
  getSelectedPhoneCallStatus,
} from "redux/slices/supplier-phone-calls";
import { SupplierPhoneCallsService } from "services/supplier-phone-calls-service";

const InboundArrow = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 4L4 12M4 12H9M4 12V7"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OutboundArrow = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 12L12 4M12 4H7M12 4V9"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlayButton = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="18" cy="18" r="17.25" stroke="#d8dcde" strokeWidth="1.5" />
    <path d="M15 12.5L24 18L15 23.5V12.5Z" fill="#68737d" />
  </svg>
);

const Section = ({ label, children }) => (
  <div className="phone-call-modal-section">
    <div className="phone-call-modal-section-label">{label}</div>
    <div className="phone-call-modal-section-value">{children}</div>
  </div>
);

const PhoneCallModal = ({ call: listCall, onClose }) => {
  const detail = useSelector(getSelectedPhoneCall);
  const status = useSelector(getSelectedPhoneCallStatus);
  const [presignedUrl, setPresignedUrl] = useState(null);
  const [presignedUrlLoading, setPresignedUrlLoading] = useState(false);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const isLoading = status === STATUS.loading;

  const attrs =
    detail?.data?.attributes || listCall?.attributes || listCall || {};
  const isInbound = attrs.direction === "inbound";
  const createdAt = attrs.telephony_supplier_created_at;

  const timeAgo = createdAt
    ? `${formatDistanceToNow(new Date(createdAt))} ago - ${formatDate(
        createdAt,
        "HH:mm:ss"
      )}`
    : "";

  const included = detail?.included || [];
  // The API is inconsistent about separators in resource types
  // (support_agent vs transcript-observation), so compare normalised.
  const normaliseType = (type) => String(type || "").replace(/-/g, "_");
  const findIncluded = (type, id) =>
    included.find(
      (i) => normaliseType(i.type) === normaliseType(type) && i.id === id
    );

  const agentRelId = detail?.data?.relationships?.support_agent?.data?.id;
  const agentName =
    findIncluded("support_agent", agentRelId)?.attributes?.name || "—";

  const transcriptRelId = detail?.data?.relationships?.transcript?.data?.id;
  const transcript = findIncluded("transcript", transcriptRelId);
  const {
    body: transcriptBody,
    agent_score: agentScore,
    agent_score_rationale: agentScoreRationale,
  } = transcript?.attributes || {};

  // Prefer the transcript's own relationship listing, but fall back to every
  // included observation - a call detail response only ever carries one
  // transcript, so the included observations all belong to it.
  const observationRelIds = transcript?.relationships?.observations?.data || [];
  const observations = observationRelIds.length
    ? observationRelIds
        .map(({ id }) => findIncluded("transcript_observation", id))
        .filter(Boolean)
    : included.filter(
        (i) => normaliseType(i.type) === "transcript_observation"
      );

  const relatedTasks = observations.reduce((tasks, observation) => {
    const taskId = observation?.attributes?.task_id;
    if (!taskId || tasks.some((task) => task.id === String(taskId)))
      return tasks;

    return [
      ...tasks,
      { id: String(taskId), body: observation.attributes.body || "" },
    ];
  }, []);

  const recordingRelId = detail?.data?.relationships?.recording?.data?.id;

  const handleLoadRecording = () => {
    if (!recordingRelId) return;
    setPresignedUrlLoading(true);
    SupplierPhoneCallsService.getPresignedUrl(recordingRelId).then(
      (response) => {
        setPresignedUrl(response?.data?.data?.attributes?.url || null);
        setPresignedUrlLoading(false);
      }
    );
  };

  const renderRecording = () => {
    if (!recordingRelId) return "—";
    if (presignedUrlLoading)
      return (
        <TailSpin
          width={20}
          height={20}
          color="#E0E0E0"
          ariaLabel="loading-recording"
        />
      );
    if (presignedUrl)
      return (
        <audio
          controls
          src={presignedUrl}
          style={{ width: "100%", marginTop: "4px" }}
        >
          <track kind="captions" />
        </audio>
      );
    return (
      <button
        className="phone-call-modal-load-recording"
        onClick={handleLoadRecording}
        aria-label="Load recording"
      >
        <PlayButton />
      </button>
    );
  };

  const customerNumber = isInbound
    ? attrs.from_number || "—"
    : attrs.to_number || "—";
  const outcome = attrs.type === "unanswered" ? "Unanswered" : "Answered";

  return (
    <div className="phone-call-modal-backdrop" onClick={handleBackdropClick}>
      <div className="phone-call-modal">
        <div className="phone-call-modal-header">
          <div>
            <div className="phone-call-modal-title">
              {isInbound ? <InboundArrow /> : <OutboundArrow />}
              {isInbound ? "Inbound call" : "Outbound call"}
            </div>
            {timeAgo && (
              <div className="phone-call-modal-subtitle">{timeAgo}</div>
            )}
          </div>
          <button className="phone-call-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {isLoading ? (
          <div className="phone-call-modal-loader">
            <TailSpin
              width={40}
              height={40}
              color="#E0E0E0"
              ariaLabel="loading"
            />
          </div>
        ) : (
          <div className="phone-call-modal-body">
            <Section label="Customer">{customerNumber}</Section>
            <Section label="User">{agentName}</Section>
            <Section label="Outcome">{outcome}</Section>
            <Section
              label={relatedTasks.length > 1 ? "Related tasks" : "Related task"}
            >
              {relatedTasks.length ? (
                <ul className="phone-call-modal-task-list">
                  {relatedTasks.map(({ id, body }) => (
                    <li key={id} className="phone-call-modal-task">
                      <a
                        className="phone-call-modal-task-link"
                        href={`${config.taskTrackerUrl}/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {id}
                      </a>
                      {body && (
                        <div className="phone-call-modal-task-body">{body}</div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                "—"
              )}
            </Section>
            <Section label="Recording">{renderRecording()}</Section>
            <Section label="Agent score">
              {agentScore != null ? (
                <>
                  <div>{agentScore}</div>
                  {agentScoreRationale && (
                    <div
                      style={{
                        color: "#68737d",
                        marginTop: "4px",
                        fontSize: "13px",
                      }}
                    >
                      {agentScoreRationale}
                    </div>
                  )}
                </>
              ) : (
                "—"
              )}
            </Section>
            <Section label="Transcription">
              {transcriptBody ? (
                <div className="phone-call-modal-transcript">
                  {transcriptBody
                    .split("–")
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((line, i) => (
                      <p key={i} className="phone-call-modal-transcript-line">
                        –{line}
                      </p>
                    ))}
                </div>
              ) : (
                "—"
              )}
            </Section>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneCallModal;
